/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import {
  composeSetters,
  DateTimeTypes,
  EntityConfig,
  entityValidationSetter,
  entityValidatorByEntityConfig,
  fieldStateByEntityConfig,
  getInvalidFields,
  isEntityInvalid,
  NumberTypes,
  PlainImmutablePageState,
  setDirty,
  setEntityField,
} from '@gh/common-shared';
import { FormMode } from '@gh/core-ui';
import { isDateInvalid, isNotNil } from '@gh/core-util';

import { first } from 'lodash';
import {
  CM_WORKSHEET_INIT, getWorksheetId,
  JobBillingWorksheet,
  jobBillingWorksheetArr,
  JobBillingWorksheetItem,
} from '@gh/builder-shared-data';
import { BuilderUserContext } from '@gh/builder-shared';
import { JobBillingWorksheetBusinessService } from '../../resources/cm-worksheet-business.service';

@Injectable()
export class CmWorksheetFormState extends PlainImmutablePageState<JobBillingWorksheet> {
  items$ = this.select(({ items }) => items);
  focusedCell$ = this.select(({ focusedCell }) => focusedCell);
  dirtyItems$ = this.select(({ items }) => items.filter((item) =>
   item.isDirty && (item.isMasterAmountDirty || item.isMasterDateDirty || item.isOfficeAmountDirty || item.isOfficeDateDirty)));
  BILLING_WORKSHEET_ENTITY_CONFIG: EntityConfig<any> = {
    officePaidAmount: {
      required: () => this.businessService.masterOrUs(),
      type: NumberTypes.SignedPrice,
    },
    masterPaidAmount: {
      required: () => this.businessService.corpAndAuNz(),
      type: NumberTypes.SignedPrice,
    },
    officeLastPaymentDate: {
      required: () => this.businessService.masterOrUs(),
      type: DateTimeTypes.ShortDate,
    },
    masterLastPaymentDate: {
      required: () => this.businessService.corpAndAuNz(),
      type: DateTimeTypes.ShortDate,
    },
  };
  originalValue: JobBillingWorksheet;
  validateBillingWorksheetItem = entityValidatorByEntityConfig(this.BILLING_WORKSHEET_ENTITY_CONFIG);
  setBillingWorksheetField = composeSetters(
    (entity: JobBillingWorksheetItem, field: keyof JobBillingWorksheetItem, value) => {
      if ((field === 'officeLastPaymentDate' || field === 'masterLastPaymentDate') && value && isDateInvalid(value)) {
        return setEntityField(entity, field, void 0);
      }
      return setEntityField(entity, field, value);
    },
    setDirty,
    entityValidationSetter(this.validateBillingWorksheetItem));
  billingWorksheetFieldState = fieldStateByEntityConfig(this.BILLING_WORKSHEET_ENTITY_CONFIG);

  constructor(
    private userContext: BuilderUserContext,
    private businessService: JobBillingWorksheetBusinessService,
  ) {
    super(CM_WORKSHEET_INIT);
  }

  get firstInvalidJob(): Maybe<JobBillingWorksheetItem> {
    return first(this.value.items.filter(isEntityInvalid));
  }

  get hasInvalidJob(): boolean {
    return isNotNil(this.firstInvalidJob);
  }

  get invalid(): boolean {
    return this.hasInvalidJob;
  }

  build(details: JobBillingWorksheet): void {
    this.setMode(FormMode.EditMode);
    this.setValue(details);
    this.originalValue = this.value;
  }

  merge(details: JobBillingWorksheet): void {
    this.setMode(FormMode.EditMode);
    this.setField('items', details.items);
    this.resetDirty();
    this.originalValue = details;
  }

  setWorksheetField(entity: JobBillingWorksheetItem, field: string, value: any): void {
    const { id } = entity;
    this.setFieldWithDirty(
      'items',
      jobBillingWorksheetArr.update(this.value.items, id, (worksheet) => this.setBillingWorksheetField(worksheet, field, value)));
  }

  focusInvalidCell(): void {
    const { firstInvalidJob } = this;

    if (firstInvalidJob) {
      const jobId = getWorksheetId(firstInvalidJob);
      const invalidFields = getInvalidFields(firstInvalidJob);
      if (invalidFields.includes('officeLastPaymentDate')) {
        this.setField('focusedCell', { id: jobId, field: 'officeLastPaymentDate' });
        return;
      }
      if (invalidFields.includes('masterLastPaymentDate')) {
        this.setField('focusedCell', { id: jobId, field: 'masterLastPaymentDate' });
        return;
      }
    }
  }

  resetFocusedCell(): void {
    this.setField('focusedCell', void 0);
  }
}
